import { useTranslation } from 'react-i18next';
import { AiDocumentationFieldEnum, Documentation } from 'graphql-common';
import DocumentationPreview from '@lib/components/DocumentationPreview/DocumentationPreview';
import { AiCredits } from 'components/AiAssistant/AiCreditsIndicator';
import { AiFieldUiState } from 'routes/Documentation/utils/getAiFieldDetectionState';
import AiFieldAssist from 'routes/Documentation/components/AiDetection/AiFieldAssist';
import useDocumentFieldDetection from 'routes/Documentation/DocumentView/utils/useDocumentFieldDetection';

type Props = {
  item: Documentation;
  onOpen: (item: Documentation) => void;
  isAiDetectionEnabled?: boolean;
  credits?: AiCredits | null;
};

// Same AiFieldAssist popover as the form/view, surfacing a document flagged
// by import detection (name not yet resolved) directly in the list — as an
// inline prefix next to the name text, so the thumbnail/size/etc. stay
// visible instead of being replaced by a bare icon while detection runs.
export default function DocumentNameCell({
  item,
  onOpen,
  isAiDetectionEnabled,
  credits,
}: Props) {
  const { t } = useTranslation();
  const { name } = useDocumentFieldDetection({
    doc: item,
    documentationVersionId: item.currentVersion?.id,
    isAiEnabled: !!isAiDetectionEnabled,
  });

  const showNameAssist =
    isAiDetectionEnabled && name.uiState !== AiFieldUiState.None;

  return (
    <DocumentationPreview
      attachment={item.currentVersion?.attachmentAttached}
      customName={item.name}
      onClick={() => onOpen(item)}
      namePrefix={
        showNameAssist ? (
          <AiFieldAssist
            field={AiDocumentationFieldEnum.Name}
            resolveSource={name.resolveSource}
            onApplyValue={name.onApplyValue}
            detectionUiState={name.uiState}
            detectingLabel={t('ai-generating-name')}
            onDismiss={name.onDismiss}
            creditsRemaining={credits}
            labels={{
              idleLabel: t('ai-generate-with-ai'),
              idleCta: t('ai-generate-name-cta'),
              regenerateCta: t('ai-regenerate'),
            }}
          />
        ) : undefined
      }
    />
  );
}
